/*
 * mc-announcement-ledger.js: F4 "The announcement ledger" (La Colonne C1).
 *
 * <mc-ledger> is a tiny custom element that keeps the single free-shipping line
 * in sync with the cart. It SUBSCRIBES to the theme's existing cartUpdate
 * pub/sub (constants.js + pubsub.js, loaded defer in <head> before this script)
 * and never publishes it. It never adds a cart publisher. The only cart read is
 * a debounced GET of the cart JSON, used only when a publisher hands us an
 * /cart/add.js payload (no totals). Full-cart payloads need no fetch.
 *
 * Content is never gated on JS: the correct state is server-rendered in
 * sections/announcement-bar.liquid; role="status" announces live changes.
 */
(function () {
  if (window.customElements && customElements.get('mc-ledger')) return;

  var DEBOUNCE_MS = 150;
  var FADE_FALLBACK_MS = 300;

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  var McLedger = class extends HTMLElement {
    connectedCallback() {
      this.msgEl = this.querySelector('.mc-ledger__msg');

      this.threshold = toInt(this.getAttribute('data-threshold'), 9900);
      if (this.threshold <= 0) this.threshold = 9900;
      this.hintMax = toInt(this.getAttribute('data-hint-max'), 0);

      this.tpl = {
        static: this.getAttribute('data-msg-static') || '',
        remaining: this.getAttribute('data-msg-remaining') || '',
        hint: this.getAttribute('data-msg-hint') || '',
        free: this.getAttribute('data-msg-free') || '',
      };

      this._token = 0;
      this._timer = null;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      this._onPageshow = function (event) {
        if (event.persisted) this.refresh();
      }.bind(this);
      window.addEventListener('pageshow', this._onPageshow);
    }

    disconnectedCallback() {
      if (this.unsub) this.unsub();
      window.removeEventListener('pageshow', this._onPageshow);
      if (this._timer) clearTimeout(this._timer);
    }

    onCartUpdate(event) {
      var d = event && event.cartData;
      // Full-cart payloads (cart-items / quick-add / quick-order-list) carry
      // totals directly, so render without a fetch. The /cart/add.js payloads
      // (product-form / cart-upsell) have no totals, so fall back to GET.
      if (d && typeof d.total_price === 'number' && typeof d.item_count === 'number') {
        this.render(d.item_count, d.total_price, d.currency);
      } else {
        this.refresh();
      }
    }

    refresh() {
      var self = this;
      if (this._timer) clearTimeout(this._timer);
      this._timer = setTimeout(function () {
        var token = ++self._token;
        var url = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
        fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            if (token !== self._token) return; // stale response, drop it
            self.render(cart.item_count, cart.total_price, cart.currency);
          })
          .catch(function () {
            /* keep the server-rendered line on failure */
          });
      }, DEBOUNCE_MS);
    }

    formatMoney(cents, whole) {
      try {
        var opts = { style: 'currency', currency: 'CAD' };
        if (whole) {
          opts.minimumFractionDigits = 0;
          opts.maximumFractionDigits = 0;
        }
        return new Intl.NumberFormat('en-CA', opts).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(whole ? 0 : 2);
      }
    }

    // Build message DOM: split the template on its placeholder and insert a
    // <b> node with the formatted amount. Never innerHTML with setting text.
    buildNodes(template, placeholder, amountText) {
      var frag = document.createDocumentFragment();
      var idx = template.indexOf(placeholder);
      if (idx === -1) {
        frag.appendChild(document.createTextNode(template));
        return frag;
      }
      var before = template.slice(0, idx);
      var after = template.slice(idx + placeholder.length);
      if (before) frag.appendChild(document.createTextNode(before));
      var b = document.createElement('b');
      b.textContent = amountText;
      frag.appendChild(b);
      if (after) frag.appendChild(document.createTextNode(after));
      return frag;
    }

    render(count, totalCents, currency) {
      if (!this.msgEl) return;
      if (typeof count !== 'number' || typeof totalCents !== 'number') return;

      var frag;
      // State 1: empty cart or non-CAD cart keeps the static resting line.
      if (count === 0 || (currency && currency !== 'CAD')) {
        var whole = this.threshold % 100 === 0;
        frag = this.buildNodes(this.tpl.static, '[threshold]', this.formatMoney(this.threshold, whole));
      } else if (totalCents >= this.threshold) {
        // State 3: already ships free.
        frag = document.createDocumentFragment();
        frag.appendChild(document.createTextNode(this.tpl.free));
      } else {
        // State 2: below threshold (CAD only).
        var remaining = this.threshold - totalCents;
        frag = this.buildNodes(this.tpl.remaining, '[amount]', this.formatMoney(remaining, false));
        if (this.hintMax > 0 && remaining <= this.hintMax && this.tpl.hint) {
          frag.appendChild(document.createTextNode(' ' + this.tpl.hint));
        }
      }

      this.swap(frag);
    }

    swap(frag) {
      // No change? Do nothing (avoids a needless flicker / SR re-announce).
      var next = frag.textContent;
      if (next === this.msgEl.textContent) return;

      var self = this;
      var replace = function () {
        self.msgEl.textContent = '';
        self.msgEl.appendChild(frag);
        self.classList.remove('mc-ledger--out');
      };

      this.classList.add('mc-ledger--out');

      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        replace();
      };
      this.msgEl.addEventListener('transitionend', finish, { once: true });
      // Fallback if no transition fires (reduced motion collapses --mc-dur).
      setTimeout(finish, FADE_FALLBACK_MS);
    }
  };

  window.customElements.define('mc-ledger', McLedger);
})();
